<template>
  <div class="qr-ticket-page">
    <div v-if="loading" class="qr-ticket-state">
      <div class="qr-ticket-spinner"></div>
      <p class="qr-ticket-state-msg">티켓 정보를 불러오는 중입니다...</p>
    </div>

    <div v-else-if="errorMessage" class="qr-ticket-state">
      <div class="qr-ticket-error-icon">!</div>
      <h2 class="qr-ticket-state-title">티켓을 표시할 수 없습니다</h2>
      <p class="qr-ticket-state-msg">{{ errorMessage }}</p>
    </div>

    <div v-else-if="reservation" class="qr-ticket-card">
      <div class="qr-ticket-header">
        <span class="qr-ticket-brand">LINKOOK LIVE</span>
        <span class="qr-ticket-badge">QR 입장 티켓</span>
      </div>

      <h1 class="qr-ticket-title">{{ liveTitle || "공연" }}</h1>
      <p v-if="liveDate || liveVenue" class="qr-ticket-subtitle">
        <span v-if="liveDate">{{ liveDate }}</span>
        <span v-if="liveDate && liveVenue" class="qr-ticket-dot">·</span>
        <span v-if="liveVenue">{{ liveVenue }}</span>
      </p>

      <div class="qr-ticket-image-wrap">
        <img v-if="qrDataUrl" :src="qrDataUrl" alt="QR 코드" class="qr-ticket-image" />
        <div v-else class="qr-ticket-image-placeholder">
          QR 코드를 생성하지 못했습니다.
        </div>
      </div>

      <div class="qr-ticket-info">
        <div class="qr-ticket-info-row">
          <span class="qr-ticket-info-label">티켓번호</span>
          <span class="qr-ticket-info-value qr-ticket-info-mono">#{{ ticketShortNo }}</span>
        </div>
        <div v-if="reservation.quantity" class="qr-ticket-info-row">
          <span class="qr-ticket-info-label">매수</span>
          <span class="qr-ticket-info-value">{{ reservation.quantity }}매</span>
        </div>
        <div class="qr-ticket-info-row">
          <span class="qr-ticket-info-label">상태</span>
          <span :class="['qr-ticket-status', `qr-ticket-status--${statusKey}`]">{{ statusLabel }}</span>
        </div>
      </div>

      <p class="qr-ticket-footer-note">
        입장 시 운영자에게 위 QR 코드를 보여주세요.
      </p>
    </div>
  </div>
</template>

<script>
import QRCode from "qrcode";
import { firestore } from "../../../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { getTicketReservationById } from "@/utils/read/ticket";

export default {
  name: "QrTicketView",
  data() {
    return {
      loading: true,
      errorMessage: "",
      reservation: null,
      liveData: null,
      qrDataUrl: "",
    };
  },
  computed: {
    ticketParam() {
      return this.$route.params.ticket || "";
    },
    ticketShortNo() {
      const id = this.reservation?.id || this.ticketParam;
      return String(id || "").slice(-8).toUpperCase();
    },
    liveTitle() {
      return this.liveData?.title || this.reservation?.liveTitle || "";
    },
    liveDate() {
      return this.liveData?.date || "";
    },
    liveVenue() {
      return this.liveData?.place || this.liveData?.venue || "";
    },
    statusKey() {
      const r = this.reservation;
      if (!r) return "unknown";
      if (r.status === "cancelled") return "cancelled";
      if (r.status === "refund_completed") return "refund";
      if (r.status === "refund_requested") return "refund";
      if (r.isInviteTicket) return "invite";
      if (r.status === "confirmed") return "confirmed";
      return r.status || "unknown";
    },
    statusLabel() {
      const r = this.reservation;
      if (!r) return "-";
      if (r.status === "cancelled") return "취소됨";
      if (r.status === "refund_completed") return "환불 완료";
      if (r.status === "refund_requested") return "환불 대기";
      if (r.isInviteTicket) return "초대권";
      if (r.status === "confirmed") return "확정";
      if (r.status === "pending") return "입금 대기";
      return r.status || "-";
    },
  },
  watch: {
    ticketParam: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadTicket();
        }
      },
    },
  },
  methods: {
    async loadTicket() {
      const ticket = (this.ticketParam || "").trim();
      this.loading = true;
      this.errorMessage = "";
      this.reservation = null;
      this.liveData = null;
      this.qrDataUrl = "";

      if (!ticket) {
        this.errorMessage = "유효하지 않은 티켓 경로입니다.";
        this.loading = false;
        return;
      }

      try {
        const reservation = await getTicketReservationById(ticket);
        if (!reservation) {
          this.errorMessage = "해당 티켓을 찾을 수 없습니다.";
          return;
        }

        this.reservation = reservation;

        if (reservation.liveId) {
          try {
            const liveDoc = await getDoc(doc(firestore, "lives", reservation.liveId));
            if (liveDoc.exists()) {
              this.liveData = liveDoc.data();
            }
          } catch (liveErr) {
            console.warn("공연 정보 조회 실패:", liveErr);
          }
        }

        await this.generateQrCode(reservation);
        this.updatePageMeta();
      } catch (err) {
        console.error("티켓 조회 오류:", err);
        if (err?.response?.status === 404) {
          this.errorMessage = "해당 티켓을 찾을 수 없습니다.";
        } else if (err?.code === "ERR_CANCELED" || err?.name === "AbortError") {
          // 라우트 이탈로 인한 취소 — 메시지 노출 X
          this.errorMessage = "";
        } else {
          this.errorMessage = "티켓 정보를 불러오는 중 오류가 발생했습니다.";
        }
      } finally {
        this.loading = false;
      }
    },
    async generateQrCode(reservation) {
      const qrValue =
        reservation?.qrValue ||
        this.buildTicketLink(reservation) ||
        JSON.stringify({
          reservationId: reservation?.id || null,
          liveId: reservation?.liveId || null,
          email: reservation?.email || null,
        });
      try {
        this.qrDataUrl = await QRCode.toDataURL(qrValue, {
          width: 320,
          margin: 1,
          errorCorrectionLevel: "M",
          color: { dark: "#0f172a", light: "#ffffff" },
        });
      } catch (e) {
        console.error("QR 생성 오류:", e);
        this.qrDataUrl = "";
      }
    },
    buildTicketLink(reservation) {
      const origin = window?.location?.origin || "";
      const liveId = reservation?.liveId || "";
      const rid = reservation?.id || "";
      if (!origin || !liveId) return "";
      return `${origin}/ticket/${liveId}?mode=view&rid=${encodeURIComponent(rid)}`;
    },
    updatePageMeta() {
      const title = this.liveTitle ? `${this.liveTitle} · QR 티켓` : "QR 티켓";
      try {
        document.title = title;
      } catch (e) {
        // ignore
      }
    },
  },
};
</script>

<style scoped>
.qr-ticket-page {
  min-height: 100vh;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  box-sizing: border-box;
}

.qr-ticket-card {
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 20px;
  padding: 28px 24px 24px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  border: 1px solid #e2e8f0;
  text-align: center;
}

.qr-ticket-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.qr-ticket-brand {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  letter-spacing: 0.06em;
  color: #6d28d9;
}

.qr-ticket-badge {
  font-size: 11px;
  padding: 4px 10px;
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.12), rgba(91, 33, 182, 0.12));
  color: #6d28d9;
  border: 1px solid rgba(124, 58, 237, 0.3);
  border-radius: 999px;
  font-family: "NanumSquareRoundB", sans-serif;
}

.qr-ticket-title {
  margin: 0 0 6px;
  font-size: 22px;
  line-height: 1.3;
  color: #0f172a;
  font-family: "NanumSquareRoundB", sans-serif;
  word-break: keep-all;
}

.qr-ticket-subtitle {
  margin: 0 0 22px;
  font-size: 14px;
  color: #475569;
  line-height: 1.5;
}

.qr-ticket-dot {
  margin: 0 6px;
  color: #cbd5e1;
}

.qr-ticket-image-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: #ffffff;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  margin-bottom: 22px;
}

.qr-ticket-image {
  width: 280px;
  max-width: 100%;
  height: auto;
  display: block;
}

.qr-ticket-image-placeholder {
  width: 280px;
  max-width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 13px;
}

.qr-ticket-info {
  text-align: left;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 18px;
}

.qr-ticket-info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #e2e8f0;
}

.qr-ticket-info-row:last-child {
  border-bottom: none;
}

.qr-ticket-info-label {
  font-size: 13px;
  color: #64748b;
}

.qr-ticket-info-value {
  font-size: 14px;
  color: #0f172a;
  font-family: "NanumSquareRoundB", sans-serif;
  text-align: right;
  word-break: break-all;
}

.qr-ticket-info-mono {
  font-family: "Menlo", "Consolas", "NanumSquareRoundB", sans-serif;
  letter-spacing: 0.04em;
}

.qr-ticket-status {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  font-family: "NanumSquareRoundB", sans-serif;
}

.qr-ticket-status--confirmed {
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.qr-ticket-status--invite {
  background: rgba(124, 58, 237, 0.12);
  color: #6d28d9;
  border: 1px solid rgba(124, 58, 237, 0.3);
}

.qr-ticket-status--cancelled,
.qr-ticket-status--refund {
  background: rgba(239, 68, 68, 0.1);
  color: #b91c1c;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.qr-ticket-status--unknown,
.qr-ticket-status--pending {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.qr-ticket-footer-note {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
}

.qr-ticket-state {
  text-align: center;
  padding: 40px 20px;
  color: #475569;
}

.qr-ticket-state-title {
  margin: 12px 0 6px;
  font-size: 18px;
  color: #0f172a;
  font-family: "NanumSquareRoundB", sans-serif;
}

.qr-ticket-state-msg {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
}

.qr-ticket-error-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 12px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.12);
  color: #b91c1c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
}

.qr-ticket-spinner {
  width: 36px;
  height: 36px;
  margin: 0 auto 14px;
  border: 3px solid #e2e8f0;
  border-top-color: #7c3aed;
  border-radius: 50%;
  animation: qr-ticket-spin 0.8s linear infinite;
}

@keyframes qr-ticket-spin {
  to { transform: rotate(360deg); }
}
</style>
